import { useEffect, useState } from 'react';

export type ImagePreloadStatus = 'loading' | 'loaded' | 'failed';

export type ImagePreloadResult = {
  status: ImagePreloadStatus;
  // Last url that finished loading successfully, so consumers can keep showing
  // the previous image while a new (cache-busted) url preloads.
  lastLoadedUrl: string | null;
};

// Preloads an image so consumers can distinguish "still loading" from "failed
// to load" — a CSS background-image gives no error signal.
export function useImagePreload(url: string | null | undefined): ImagePreloadResult {
  const [status, setStatus] = useState<ImagePreloadStatus>('loading');
  const [lastLoadedUrl, setLastLoadedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setStatus('failed');
      return;
    }

    let cancelled = false;
    setStatus('loading');

    const image = new Image();
    image.onload = () => {
      if (cancelled) return;
      setStatus('loaded');
      setLastLoadedUrl(url);
    };
    image.onerror = () => {
      if (!cancelled) setStatus('failed');
    };
    image.src = url;

    return () => {
      cancelled = true;
      image.onload = null;
      image.onerror = null;
    };
  }, [url]);

  return { status, lastLoadedUrl };
}
